/**
 * ConversationList — the left pane of the Mobile Forensics view.
 *
 * A messenger-style index of every reconstructed thread: monogram avatar,
 * title, last-message preview, activity time and message count. Search and
 * the per-application chips are this pane's own state — the host resets them
 * by keying the component on the extraction id.
 */

import { type FC, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterChip } from '@/components/ui/filter-chip'
import { SearchInput } from '@/components/ui/search-input'
import type { CommsConversation, CommsMessage } from '@/services/api/comms'
import { cn } from '@/utils/cn'
import {
  appLabel,
  conversationInitials,
  conversationTitle,
  DIRECTION_OUT,
  isGroup,
  listTime,
} from './comms-format'

/** Enough context around a hit to recognise it without opening the thread. */
const SNIPPET_LEAD = 32
const SNIPPET_LEN = 140

interface ConversationListProps {
  conversations: CommsConversation[]
  activeId: string | null
  onSelect: (conversationId: string) => void
  /**
   * The search box is controlled so one input drives both halves of search:
   * this pane filters thread names locally, while the host runs the same
   * string as a full-text query over message bodies and feeds the hits back
   * in as `matches`.
   */
  query: string
  onQueryChange: (query: string) => void
  fuzzy: boolean
  onFuzzy: (fuzzy: boolean) => void
  matches: CommsMessage[]
  /** The server capped the hits — say so rather than imply this is all of them. */
  matchesHasMore: boolean
  matchesLoading: boolean
  onSelectMatch: (message: CommsMessage) => void
}

/** Window the body around the first hit, so the match is visible in one line. */
function snippet(body: string, needle: string): string {
  const at = body.toLowerCase().indexOf(needle)
  if (at <= SNIPPET_LEAD) return body.slice(0, SNIPPET_LEN)
  return `…${body.slice(at - SNIPPET_LEAD, at - SNIPPET_LEAD + SNIPPET_LEN)}`
}

const MatchRow = memo(function MatchRow({
  message,
  title,
  needle,
  onSelect,
}: {
  message: CommsMessage
  title: string
  needle: string
  onSelect: (message: CommsMessage) => void
}) {
  const { i18n } = useTranslation()
  return (
    <button
      type="button"
      onClick={() => onSelect(message)}
      className="flex w-full flex-col gap-0.5 border-b border-border/40 px-3 py-2 text-left transition-colors hover:bg-muted/50"
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="truncate text-xs font-medium text-foreground">{title}</span>
        <span className="shrink-0 font-mono text-2xs text-muted-foreground">
          {listTime(message.sentAt, i18n.language)}
        </span>
      </span>
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {snippet(message.body ?? '', needle)}
      </span>
    </button>
  )
})

const Row = memo(function Row({
  conversation,
  active,
  onSelect,
}: {
  conversation: CommsConversation
  active: boolean
  onSelect: (conversationId: string) => void
}) {
  const { t, i18n } = useTranslation()
  const preview = conversation.lastMessage
  const previewText =
    preview?.body?.trim() ||
    (preview?.msgType
      ? t(`comms.msgType.${preview.msgType}`, { defaultValue: preview.msgType })
      : '')
  return (
    <button
      type="button"
      onClick={() => onSelect(conversation.conversationId)}
      aria-current={active || undefined}
      className={cn(
        'flex w-full items-center gap-3 border-b border-border/40 px-3 py-2.5 text-left transition-colors',
        active ? 'bg-muted' : 'hover:bg-muted/50'
      )}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-xl text-xs font-semibold',
          active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
        )}
      >
        {conversationInitials(conversation)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {conversationTitle(conversation)}
          </span>
          <span className="shrink-0 font-mono text-2xs text-muted-foreground">
            {listTime(conversation.threadEnd, i18n.language)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-muted-foreground">
            {preview?.direction === DIRECTION_OUT && (
              <span className="text-foreground/70">{t('comms.list.you')} </span>
            )}
            {previewText || ' '}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {isGroup(conversation) && (
              <Badge variant="outline" className="h-4 px-1 text-2xs">
                {conversation.participants.length}
              </Badge>
            )}
            <Badge variant="secondary" className="h-4 px-1 font-mono text-2xs">
              {conversation.messageCount}
            </Badge>
          </span>
        </span>
      </span>
    </button>
  )
})

export const ConversationList: FC<ConversationListProps> = ({
  conversations,
  activeId,
  onSelect,
  query,
  onQueryChange,
  fuzzy,
  onFuzzy,
  matches,
  matchesHasMore,
  matchesLoading,
  onSelectMatch,
}) => {
  const { t } = useTranslation()
  const [appFilter, setAppFilter] = useState<string | null>(null)

  const appCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const conversation of conversations) {
      counts.set(conversation.app, (counts.get(conversation.app) ?? 0) + 1)
    }
    return counts
  }, [conversations])

  const needle = query.trim().toLowerCase()

  /** Thread titles for the hits, so a result reads as "who" not just "what". */
  const titles = useMemo(
    () => new Map(conversations.map(c => [c.conversationId, conversationTitle(c)])),
    [conversations]
  )
  const visible = useMemo(
    () =>
      conversations.filter(conversation => {
        if (appFilter && conversation.app !== appFilter) return false
        if (!needle) return true
        const haystack = `${conversationTitle(conversation)} ${conversation.participants.join(' ')}`
        return haystack.toLowerCase().includes(needle)
      }),
    [conversations, appFilter, needle]
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-3 pb-2 pt-3">
        <SearchInput
          value={query}
          onValueChange={onQueryChange}
          placeholder={t('comms.list.searchPlaceholder')}
          containerClassName="h-9 md:h-8"
        />
        {/* Offered only once there is a query: it widens *these* results, and
            an always-on switch would read as a global setting. */}
        {needle && (
          <div className="flex flex-wrap items-center gap-1 pt-1.5">
            <Button
              variant={fuzzy ? 'secondary' : 'ghost'}
              size="sm"
              aria-pressed={fuzzy}
              onClick={() => onFuzzy(!fuzzy)}
              className="h-8 rounded-full px-2.5 text-2xs md:h-6"
            >
              {t('comms.list.fuzzy')}
            </Button>
            <span className="text-2xs text-muted-foreground">{t('comms.list.fuzzyHint')}</span>
          </div>
        )}
      </div>
      {appCounts.size > 1 && (
        <div className="flex flex-wrap gap-1 px-3 pb-2">
          <FilterChip
            label={t('comms.list.allApps')}
            count={conversations.length}
            active={appFilter === null}
            onClick={() => setAppFilter(null)}
          />
          {[...appCounts.keys()].sort().map(app => (
            <FilterChip
              key={app}
              label={appLabel(app, t)}
              count={appCounts.get(app) ?? 0}
              active={appFilter === app}
              onClick={() => setAppFilter(app)}
            />
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {visible.map(conversation => (
          <Row
            key={conversation.conversationId}
            conversation={conversation}
            active={conversation.conversationId === activeId}
            onSelect={onSelect}
          />
        ))}
        {visible.length === 0 && !needle && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            {t('comms.list.empty')}
          </p>
        )}

        {/* Full-text hits sit under the thread matches, the way a messenger
            splits "Chats" from "Messages" — the same string answers both
            questions, and the second one needs the server. */}
        {needle && (
          <>
            <p className="sticky top-0 z-10 border-b border-border/60 bg-background/95 px-3 py-1.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">
              {t('comms.list.messageMatches')}
              {matchesHasMore && ` · ${t('comms.list.matchesCapped')}`}
            </p>
            {matchesLoading && (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {t('common.loading')}
              </p>
            )}
            {!matchesLoading &&
              matches.map(message => (
                <MatchRow
                  key={message.messageId}
                  message={message}
                  title={titles.get(message.conversationId) ?? message.conversationId}
                  needle={needle}
                  onSelect={onSelectMatch}
                />
              ))}
            {!matchesLoading && matches.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {t('comms.list.noMatches')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
